import { useEffect, useRef } from 'react';
import { RECURLY_ELEMENT_STYLE, RECURLY_OBSERVER_TIMEOUT } from '../constants';

/**
 * Custom hook to manage Recurly Elements setup
 * @param {Object} options - Hook options
 * @param {string} options.paymentMethod - Current payment method ('card' or 'bank')
 * @returns {Object} Recurly refs and helpers
 */
export const useRecurlySetup = ({ paymentMethod }) => {
  const attachedElementsRef = useRef({
    cardNumber: false,
    cardMonth: false,
    cardYear: false,
    cardCvv: false,
    accountNumber: false,
    routingNumber: false,
    accountType: false,
    accountHolderName: false
  });
  const elementsRef = useRef(null);
  const observerRef = useRef(null);

  useEffect(() => {
    const recurly = window.recurly;
    const publicKey = process.env.REACT_APP_RECURLY_PUBLIC_KEY || 'ewr1-uKloW7PK9B1AXHiGZZ3OVb';
    recurly.configure(publicKey);

    // Only create Elements instance if it doesn't exist
    if (!elementsRef.current) {
      elementsRef.current = recurly.Elements();
    }

    if (paymentMethod === 'card') {
      // Set up a mutation observer to catch when iframes are added and immediately blur them
      observerRef.current = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const iframes = node.querySelectorAll ? node.querySelectorAll('iframe') : [];
              iframes.forEach(iframe => {
                iframe.setAttribute('tabindex', '-1');
                iframe.blur();
              });

              // If the node itself is an iframe
              if (node.tagName === 'IFRAME') {
                node.setAttribute('tabindex', '-1');
                node.blur();
              }
            }
          });
        });
      });

      // Start observing the payment method container
      const paymentContainer = document.querySelector('.payment-method-selection');
      if (paymentContainer) {
        observerRef.current.observe(paymentContainer, { childList: true, subtree: true });
      }

      // Only create and attach if not already attached
      if (!attachedElementsRef.current.cardNumber) {
        const cardNumber = elementsRef.current.CardNumberElement({
          style: { ...RECURLY_ELEMENT_STYLE }
        });
        cardNumber.attach('#recurly-card-number');
        attachedElementsRef.current.cardNumber = true;
      }

      if (!attachedElementsRef.current.cardMonth) {
        const cardMonth = elementsRef.current.CardMonthElement({
          style: {
            ...RECURLY_ELEMENT_STYLE,
            placeholder: {
              content: 'MM'
            }
          }
        });
        cardMonth.attach('#recurly-card-month');
        attachedElementsRef.current.cardMonth = true;
      }

      if (!attachedElementsRef.current.cardYear) {
        const cardYear = elementsRef.current.CardYearElement({
          style: {
            ...RECURLY_ELEMENT_STYLE,
            placeholder: {
              content: 'YYYY'
            }
          }
        });
        cardYear.attach('#recurly-card-year');
        attachedElementsRef.current.cardYear = true;
      }

      if (!attachedElementsRef.current.cardCvv) {
        const cardCvv = elementsRef.current.CardCvvElement({
          style: {
            ...RECURLY_ELEMENT_STYLE,
            placeholder: {
              content: 'CVV'
            }
          }
        });
        cardCvv.attach('#recurly-card-cvv');
        attachedElementsRef.current.cardCvv = true;
      }

      // Disconnect the observer after a reasonable time
      setTimeout(() => {
        if (observerRef.current) {
          observerRef.current.disconnect();
        }
      }, RECURLY_OBSERVER_TIMEOUT);
    } else if (paymentMethod === 'bank') {
      // Disconnect the observer if it exists
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      Object.keys(attachedElementsRef.current).forEach(key => {
        attachedElementsRef.current[key] = false;
      });
    }

    // Cleanup function
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [paymentMethod]);

  return {
    elementsRef,
    attachedElementsRef
  };
};

export default useRecurlySetup;
